import { pitchFonts } from './typography';

// Color values reference CSS variables set globally in App.tsx from the active
// design template (src/data/designTemplates.ts); fallbacks are the Ocean Blue palette.
const pitchColors = {
  blueLight: 'var(--pitch-blue-light, #8BC7E0)',
  blue: 'var(--pitch-blue, #44A1EA)',
  blueDark: 'var(--pitch-blue-dark, #4779BD)',
  navy: 'var(--pitch-navy, #232E50)',
  navyDeep: 'var(--pitch-navy-deep, #051554)',
  cream: 'var(--pitch-cream, #FAFAF7)',
  accent: 'var(--pitch-accent, #8BC7E0)',
  text: 'var(--pitch-text, #051554)',
  textMuted: 'var(--pitch-text-muted, #4779BD)',
  border: 'var(--pitch-border, #CFE3F1)',
  gradient: 'var(--pitch-gradient, linear-gradient(160deg, #8BC7E0 0%, #44A1EA 100%))',
  pageGradient: 'var(--pitch-page-gradient, linear-gradient(180deg, #EAF4FA 0%, #D4E8F7 100%))',
} as const;

/** App shell / marketing UI — Anchor-inspired fintech palette (independent of deck themes). */
export const appTheme = {
  green: '#134E44',
  greenDark: '#0D3B34',
  greenHover: '#1A5C50',
  cream: '#FAFAF8',
  text: '#0A2E28',
  textMuted: '#5C7168',
  textOnDark: 'rgba(255, 255, 255, 0.88)',
  textMutedOnDark: 'rgba(255, 255, 255, 0.62)',
  border: '#E2E8E5',
  borderOnDark: 'rgba(255, 255, 255, 0.12)',
  orange: '#D4652C',
  orangeHover: '#B85524',
  mint: '#E8F5F0',
  mintDark: '#134E44',
  lavender: '#EDE8F5',
  lavenderDark: '#4A3D6B',
  peach: '#FAE8DC',
  peachDark: '#8B4A2E',
  violet: '#2D2448',
  footerBg: '#0A2E28',
  pageBg: '#FAFAF8',
  surface: '#ffffff',
  surfaceHover: '#F3F6F5',
  radiusSm: '8px',
  radiusMd: '12px',
  radiusLg: '20px',
  radiusPill: '999px',
  shadowSm: '0 1px 2px rgba(10, 46, 40, 0.05)',
  shadowMd: '0 8px 24px rgba(10, 46, 40, 0.07)',
  shadowLg: '0 16px 48px rgba(10, 46, 40, 0.1)',
  maxWidth: '1200px',
  dangerText: '#b3372b',
  dangerBg: '#fdecec',
  dangerBorder: '#f0d0cc',
  successText: '#1e7a4f',
  successBg: '#e9f7f0',
} as const;

// Non-palette UI tokens: surfaces, semantic states, radii, and elevation.
const pitchUi = {
  surface: '#ffffff',
  surfaceHover: '#f2f8fd',
  surfaceSubtle: '#fafcfe',
  dangerText: appTheme.dangerText,
  dangerBg: appTheme.dangerBg,
  dangerBorder: appTheme.dangerBorder,
  successText: appTheme.successText,
  successBg: appTheme.successBg,
  radiusSm: appTheme.radiusSm,
  radiusMd: appTheme.radiusMd,
  radiusLg: appTheme.radiusLg,
  radiusPill: appTheme.radiusPill,
  shadowSm: appTheme.shadowSm,
  shadowMd: appTheme.shadowMd,
  shadowLg: appTheme.shadowLg,
} as const;

/** Unified theme: colors + UI tokens + typography. Use in styled-components. */
export const pitchTheme = {
  ...pitchColors,
  ...pitchUi,
  fontContent: pitchFonts.content,
  fontHeading: pitchFonts.heading,
  fontPageTitle: pitchFonts.pageTitle,
  fontUi: pitchFonts.ui,
};

export type PitchTheme = typeof pitchTheme;
